import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { lumi } from "../lib/lumi"
import { useAuth } from "../hooks/useAuth"
import ReactQuill, { Quill } from "react-quill"
import "react-quill/dist/quill.snow.css"
import ImageResize from "quill-image-resize-module-react"
import {FileText, Calendar, Save, Printer, CheckCircle, Clock, Send, Image as ImageIcon, Paperclip, X, Link2, Copy, ClipboardPaste} from 'lucide-react'
import toast from "react-hot-toast"
import { IssueLinkPickerModal } from "./IssueLinkPickerModal"
import { LinkedIssueBadges } from "./LinkedIssueBadges"
import type { LinkedIssue } from "../lib/issueLink"
import { setPendingIssueTarget } from "../lib/issueLink"

// Quill에 이미지 리사이즈 모듈 등록
Quill.register('modules/imageResize', ImageResize)

// Quill Size format에 px 단위 whitelist 설정
const Size = Quill.import('formats/size')
// 16px를 첫 번째에 배치하여 기본값으로 사용
Size.whitelist = ['16px', '8px', '9px', '10px', '11px', '12px', '14px', '18px', '24px', '36px', '48px']
Quill.register(Size, true)

const ACTIVITY_COLORS = {
  "개발/설계": "#3B82F6",
  "영업/견적/PM/기획": "#10B981",
  "제작/조립/구매": "#F59E0B",
  "시험/검증/테스트": "#8B5CF6",
  "설치/납품/AS": "#EF4444",
  "지원/문서/교육": "#EC4899",
  "미팅": "#6366F1"
}

export function WeeklyReport() {
  const { user, isCLevel } = useAuth()
  const [reports, setReports] = useState<any[]>([])
  const [selectedWeek, setSelectedWeek] = useState<Date>(getMonday(new Date()))
  const [content, setContent] = useState("")
  // 방안 A: 금주 실적 / 차주 계획 2단 분리
  const [thisWeekContent, setThisWeekContent] = useState("")
  const [nextWeekPlan, setNextWeekPlan] = useState("")
  // 지난주 보고서의 차주 계획(참고용)
  const [prevNextWeekPlan, setPrevNextWeekPlan] = useState("")
  // 방안 A: 주간보고에 연결된 장비 이슈 목록
  const [linkedIssues, setLinkedIssues] = useState<LinkedIssue[]>([])
  const [showIssuePicker, setShowIssuePicker] = useState(false)
  const [thisWeekHeight, setThisWeekHeight] = useState<number>(384) // 금주 실적 에디터 높이(px)
  const [nextWeekHeight, setNextWeekHeight] = useState<number>(320) // 차주 계획 에디터 높이(px)
  const [currentReport, setCurrentReport] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [timeEntries, setTimeEntries] = useState<any[]>([])
  const printRef = useRef<HTMLDivElement>(null)
  const quillRef = useRef<any>(null)
  const quillRefNext = useRef<any>(null)
  
  // 이미지 업로드 관련 상태
  const [uploadingImage, setUploadingImage] = useState(false)
  const uploadingRef = useRef(false) // ref로 업로드 상태 추적
  const MAX_IMAGES = 10
  const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

  // 첨부파일 관련 상태
  const [attachments, setAttachments] = useState<any[]>([])
  const [uploadingFile, setUploadingFile] = useState(false)
  const MAX_ATTACHMENTS = 5
  const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024 // 10MB (저장 용량 절약)
  const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/zip',
    'application/x-zip-compressed',
    'application/x-zip',
    'multipart/x-zip',
    'application/haansofthwp',
    'application/vnd.hancom.hwp',
    'application/vnd.hancom.hwpx',
    'application/x-hwp',
    'application/hwp',
    'text/plain',
    'image/png',
    'image/jpeg'
  ]

  // HWP/ZIP은 브라우저/OS에 따라 MIME 타입이 비어 있거나 제각각이므로 확장자 기준으로도 허용
  const ALLOWED_FILE_EXTENSIONS = [
    'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
    'zip', 'hwp', 'hwpx', 'txt', 'png', 'jpg', 'jpeg'
  ]

  const getFileExtension = (fileName: string): string => {
    const idx = fileName.lastIndexOf('.')
    return idx >= 0 ? fileName.slice(idx + 1).toLowerCase() : ''
  }

  // MIME 타입 또는 확장자 중 하나만 통과해도 허용 (HWP 호환성 확보)
  const isAllowedFile = (file: File): boolean => {
    const ext = getFileExtension(file.name)
    if (ALLOWED_FILE_EXTENSIONS.includes(ext)) return true
    return !!file.type && ALLOWED_FILE_TYPES.includes(file.type)
  }

  // file.type이 비어 있는 경우(주로 HWP) 확장자로 타입을 보정하여 저장
  const resolveFileType = (file: File): string => {
    if (file.type) return file.type
    const ext = getFileExtension(file.name)
    if (ext === 'hwp') return 'application/haansofthwp'
    if (ext === 'hwpx') return 'application/vnd.hancom.hwpx'
    if (ext === 'zip') return 'application/zip'
    return 'application/octet-stream'
  }

  useEffect(() => {
    loadUserProfile()
  }, [user])

  useEffect(() => {
    if (user) {
      loadWeeklyReport()
      loadTimeEntries()
    }
  }, [selectedWeek, user])

  // 페이지 focus 시 투입시간 자동 갱신 (주간보고 제출 후 투입시간이 뒤늦게 업데이트되는 경우 대응)
  useEffect(() => {
    const handleFocus = () => {
      if (user) {
        loadTimeEntries()
      }
    }
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [user, selectedWeek])

  // 첨부파일 로드
  useEffect(() => {
    if (currentReport?._id) {
      loadAttachments(currentReport._id)
    } else {
      setAttachments([])
    }
  }, [currentReport?._id])

  const [userProfile, setUserProfile] = useState<any>(null)
  const [approvalManagerName, setApprovalManagerName] = useState<string>("")
  const [rejectionReason, setRejectionReason] = useState<string>("")
  const [rejectionInfo, setRejectionInfo] = useState<any>(null)
  const [managerRejection, setManagerRejection] = useState<any>(null)
  const [ceoRejection, setCeoRejection] = useState<any>(null)
  const [ceoApproverName, setCeoApproverName] = useState<string>("")

  const loadUserProfile = async () => {
    if (!user) {
      console.error("loadUserProfile: user is not available")
      return
    }
    try {
      console.log("Loading user profile for userId:", user.userId)
      const result = await lumi.entities.userProfiles3.list({
        filter: { userId: user.userId },
        limit: 1
      })
      console.log("User profile result:", result)
      if (result.list.length > 0) {
        setUserProfile(result.list[0])
        console.log("User profile loaded successfully:", result.list[0])
      } else {
        console.error("No user profile found for userId:", user.userId)
      }
    } catch (error) {
      console.error("Failed to load user profile:", error)
    }
  }

  const loadWeeklyReport = async () => {
    if (!user) return
    setLoading(true)
    try {
      const weekStartDate = new Date(selectedWeek)
      weekStartDate.setHours(0, 0, 0, 0)
      const weekStart = weekStartDate.toISOString()
      
      const weekEndDate = getWeekEnd(selectedWeek)
      weekEndDate.setHours(0, 0, 0, 0)
      const weekEnd = weekEndDate.toISOString()

      const result = await lumi.entities.weeklyReports.list({
        filter: {
          userId: user.userId,
          weekStart,
          weekEnd
        },
        limit: 1
      })

      // 지난주 차주 계획 참고용 조회 (병렬) - 이번 주 보고서 유무와 무관하게 항상 로드
      loadPrevNextWeekPlan()

      if (result.list.length > 0) {
        const report = result.list[0]
        setCurrentReport(report)
        // 방안 A: 신규 2단 필드 우선, 없으면 레거시 content를 금주 실적으로 마이그레이션
        const hasNewFields = !!(report.thisWeekContent || report.nextWeekPlan)
        setThisWeekContent(hasNewFields ? (report.thisWeekContent || "") : (report.content || ""))
        setNextWeekPlan(report.nextWeekPlan || "")
        setContent(report.content || "")
        setLinkedIssues(Array.isArray(report.linkedIssues) ? report.linkedIssues : [])
        
        // 반려된 경우 반려 사유 조회 (팀장 반려: rejection_reasons, CEO 반려: rejection_reasons2)
        if (report.managerApproval === "rejected" || report.ceoApproval === "rejected") {
          // 팀장 반려 사유 조회 (rejection_reasons)
          if (report.managerApproval === "rejected") {
            try {
              const managerRejectionResult = await lumi.entities.rejection_reasons.list({
                filter: {
                  reportId: report._id
                }
              })
              
              if (managerRejectionResult.list.length > 0) {
                setManagerRejection(managerRejectionResult.list[0])
                console.log("팀장 반려 사유 조회 성공:", managerRejectionResult.list[0])
              } else {
                setManagerRejection(null)
              }
            } catch (error) {
              console.error("Failed to load manager rejection reason:", error)
              setManagerRejection(null)
            }
          } else {
            setManagerRejection(null)
          }
          
          // CEO 반려 사유 조회 (rejection_reasons2)
          if (report.ceoApproval === "rejected") {
            try {
              const ceoRejectionResult = await lumi.entities.rejection_reasons2.list({
                filter: {
                  reportId: report._id
                }
              })
              
              if (ceoRejectionResult.list.length > 0) {
                setCeoRejection(ceoRejectionResult.list[0])
                console.log("CEO 반려 사유 조회 성공:", ceoRejectionResult.list[0])
              } else {
                setCeoRejection(null)
              }
            } catch (error) {
              console.error("Failed to load CEO rejection reason:", error)
              setCeoRejection(null)
            }
          } else {
            setCeoRejection(null)
          }
        } else {
          setRejectionReason("")
          setRejectionInfo(null)
          setManagerRejection(null)
          setCeoRejection(null)
        }
        
        // 작성자가 소속된 팀의 팀장 이름 조회
        if (report.teamId && report.managerApproval === "approved") {
          try {
            // 작성자가 소속된 팀의 MANAGER 역할 사용자 찾기
            const managerResult = await lumi.entities.userProfiles3.list({
              filter: { 
                teamId: report.teamId,
                role: "MANAGER",
                enabled: true
              },
              limit: 1
            })
            if (managerResult.list.length > 0) {
              setApprovalManagerName(managerResult.list[0].userName || "")
              console.log("팀장 이름 조회 성공:", managerResult.list[0].userName)
            } else {
              console.log("팀장을 찾을 수 없음 - teamId:", report.teamId)
              setApprovalManagerName("")
            }
          } catch (error) {
            console.error("Failed to load manager name:", error)
            setApprovalManagerName("")
          }
        } else {
          setApprovalManagerName("")
        }
        
        // CEO 승인자 이름 조회 (CEO 팀 소속 사용자)
        if (report.ceoApproval === "approved") {
          try {
            const ceoResult = await lumi.entities.userProfiles3.list({
              filter: {
                teamName: "CEO",
                enabled: true
              },
              limit: 1
            })
            if (ceoResult.list.length > 0) {
              setCeoApproverName(ceoResult.list[0].userName || "")
              console.log("CEO 승인자 이름 조회 성공 (CEO 팀):", ceoResult.list[0].userName)
            } else {
              console.log("CEO 팀 소속 사용자를 찾을 수 없음")
              setCeoApproverName("")
            }
          } catch (error) {
            console.error("Failed to load CEO approver name:", error)
            setCeoApproverName("")
          }
        } else {
          setCeoApproverName("")
        }
      } else {
        setCurrentReport(null)
        setContent("")
        setThisWeekContent("")
        setNextWeekPlan("")
        setLinkedIssues([])
        setApprovalManagerName("")
        setCeoApproverName("")
        setRejectionReason("")
        setRejectionInfo(null)
        setManagerRejection(null)
        setCeoRejection(null)
        setAttachments([])
      }
    } catch (error) {
      console.error("Failed to load report:", error)
      toast.error("주간보고를 불러오는데 실패했습니다")
    } finally {
      setLoading(false)
    }
  }

  // 지난주 보고서의 차주 계획(nextWeekPlan)을 조회해 참고용으로 표시
  const loadPrevNextWeekPlan = async () => {
    if (!user) return
    try {
      const prevMonday = new Date(selectedWeek)
      prevMonday.setDate(prevMonday.getDate() - 7)
      prevMonday.setHours(0, 0, 0, 0)
      const prevWeekStart = prevMonday.toISOString()

      const prevSunday = getWeekEnd(prevMonday)
      prevSunday.setHours(0, 0, 0, 0)
      const prevWeekEnd = prevSunday.toISOString()

      const result = await lumi.entities.weeklyReports.list({
        filter: {
          userId: user.userId,
          weekStart: prevWeekStart,
          weekEnd: prevWeekEnd
        },
        limit: 1
      })

      if (result.list.length > 0) {
        setPrevNextWeekPlan(result.list[0].nextWeekPlan || "")
      } else {
        setPrevNextWeekPlan("")
      }
    } catch (error) {
      console.error("Failed to load previous week plan:", error)
      setPrevNextWeekPlan("")
    }
  }

  const loadAttachments = async (reportId: string) => {
    try {
      const result = await lumi.entities.attachments.list({
        filter: { reportId },
        sort: { uploadedAt: 1 }
      })
      setAttachments(result.list || [])
    } catch (error) {
      console.error("Failed to load attachments:", error)
    }
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    if (!currentReport?._id) {
      toast.error("보고서 내용 작성 후, 임시저장을 누른 뒤 파일을 업로드하세요.")
      return
    }

    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      toast.error(`최대 ${MAX_ATTACHMENTS}개의 파일만 첨부할 수 있습니다`)
      return
    }

    // 파일 검증
    const validFiles: File[] = []
    for (let i = 0; i < files.length; i++) {
      const file = files[i]

      if (!isAllowedFile(file)) {
        toast.error(`${file.name}: 지원하지 않는 파일 형식입니다`)
        continue
      }

      if (file.size > MAX_ATTACHMENT_SIZE) {
        toast.error(`${file.name}: 파일 크기는 10MB 이하여야 합니다`)
        continue
      }

      validFiles.push(file)
    }

    if (validFiles.length === 0) return

    setUploadingFile(true)
    const loadingToast = toast.loading(`파일 업로드 중... (0/${validFiles.length})`)

    try {
      const uploadResults = await lumi.tools.file.upload(validFiles)
      let successCount = 0

      for (let i = 0; i < uploadResults.length; i++) {
        const result = uploadResults[i]
        const file = validFiles[i]
        toast.loading(`파일 업로드 중... (${i + 1}/${validFiles.length})`, { id: loadingToast })

        if (result.fileUrl) {
          await lumi.entities.attachments.create({
            reportId: currentReport._id,
            fileName: result.fileName || file.name,
            fileUrl: result.fileUrl,
            fileSize: file.size,
            fileType: resolveFileType(file),
            uploadedAt: new Date().toISOString(),
            uploadedBy: user?.userId || ''
          })
          successCount++
        }
      }

      toast.dismiss(loadingToast)
      if (successCount > 0) {
        toast.success(`${successCount}개의 파일이 업로드되었습니다`)
        await loadAttachments(currentReport._id)
      }
    } catch (error: any) {
      console.error('File upload error:', error)
      toast.dismiss(loadingToast)
      const rawMsg = (error?.message || error?.data?.error || '').toString().toLowerCase()
      if (rawMsg.includes('quota')) {
        toast.error('파일 저장 용량 한도를 초과하여 업로드할 수 없습니다. 기존 첨부파일을 삭제하거나 관리자에게 문의해 주세요.', { duration: 6000 })
      } else {
        toast.error('파일 업로드 중 오류가 발생했습니다')
      }
    } finally {
      setUploadingFile(false)
      event.target.value = ''
    }
  }

  const handleDeleteAttachment = async (attachment: any) => {
    if (!window.confirm(`${attachment.fileName}을(를) 삭제하시겠습니까?`)) return

    try {
      await lumi.tools.file.delete([attachment.fileUrl])
      await lumi.entities.attachments.delete(attachment._id)
      toast.success('파일이 삭제되었습니다')
      setAttachments(attachments.filter(a => a._id !== attachment._id))
    } catch (error) {
      console.error('File delete error:', error)
      toast.error('파일 삭제에 실패했습니다')
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const getFileIcon = (fileType: string, fileName?: string): string => {
    const type = (fileType || '').toLowerCase()
    const ext = fileName ? getFileExtension(fileName) : ''
    if (ext === 'hwp' || ext === 'hwpx' || type.includes('hwp') || type.includes('hancom')) return '📕'
    if (ext === 'zip' || type.includes('zip')) return '🗜️'
    if (type.includes('pdf') || ext === 'pdf') return '📄'
    if (type.includes('word') || ext === 'doc' || ext === 'docx') return '📝'
    if (type.includes('excel') || type.includes('sheet') || ext === 'xls' || ext === 'xlsx') return '📊'
    if (type.includes('powerpoint') || type.includes('presentation') || ext === 'ppt' || ext === 'pptx') return '📽️'
    if (type.includes('image') || ['png', 'jpg', 'jpeg'].includes(ext)) return '🖼️'
    return '📎'
  }

  const loadTimeEntries = async () => {
    if (!user) return
    try {
      const weekStartDate = new Date(selectedWeek)
      weekStartDate.setHours(0, 0, 0, 0)
      const weekStart = weekStartDate.toISOString()

      // A 최적화: weekStart를 서버 필터에 포함하여 해당 주차 데이터만 조회 (전체 전송량 감소)
      const result = await lumi.entities.time_entries2.list({
        filter: {
          userId: user.userId,
          status: "submitted",
          weekStart
        }
      })

      setTimeEntries(result.list || [])
    } catch (error) {
      console.error("Failed to load time entries:", error)
    }
  }

  // HTML 문자열의 실제 텍스트가 비어있는지 검사 (빈 태그/공백만 있는 경우 포함)
  const isHtmlEmpty = (html: string): boolean => {
    if (!html) return true
    const text = html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/\u200b/g, "")
      .trim()
    return text.length === 0
  }

  const saveReport = async (submit = false) => {
    if (!user || !userProfile) return
    
    if (isHtmlEmpty(thisWeekContent)) {
      toast.error("금주 실적 내용을 입력해주세요")
      return
    }

    // 제출 시에는 차주 계획을 필수로 요구 (임시저장은 허용)
    if (submit && isHtmlEmpty(nextWeekPlan)) {
      toast.error("제출하려면 차주 계획을 입력해주세요")
      return
    }

    if (submit && currentReport?.status === "submitted" && currentReport?.managerApproval !== "rejected" && currentReport?.ceoApproval !== "rejected") {
      toast.error("이미 제출된 보고서입니다")
      return
    }

    setLoading(true)
    try {
      const weekStartDate = new Date(selectedWeek)
      weekStartDate.setHours(0, 0, 0, 0)
      const weekStart = weekStartDate.toISOString()
      
      const weekEndDate = getWeekEnd(selectedWeek)
      weekEndDate.setHours(0, 0, 0, 0)
      const weekEnd = weekEndDate.toISOString()
      
      const now = new Date().toISOString()

      // 레거시 content(통합 본문): 금주 실적/차주 계획을 결합해 하위호환 유지
      const combinedContent = `${thisWeekContent || ""}${
        isHtmlEmpty(nextWeekPlan)
          ? ""
          : `<h3>차주 계획</h3>${nextWeekPlan}`
      }`

      const reportData = {
        userId: user.userId,
        userName: userProfile.userName,
        teamId: userProfile.teamId,
        teamName: userProfile.teamName,
        weekStart,
        weekEnd,
        content: combinedContent,
        thisWeekContent,
        nextWeekPlan,
        linkedIssues,
        status: submit ? "submitted" : (currentReport?.status || "draft"),
        submittedAt: submit ? now : (currentReport?.submittedAt || ""),
        // C-Level 사용자는 팀장 승인 불필요 - 자동 승인
        managerApproval: isCLevel ? "approved" : (submit ? "pending" : (currentReport?.managerApproval || "pending")),
        isApproved: isCLevel ? true : false,
        approvedBy: isCLevel ? user.userId : "",
        approvedAt: isCLevel ? now : "",
        // CEO 반려 후 재제출 시 CEO 승인도 다시 pending으로 초기화
        ceoApproval: submit ? "pending" : (currentReport?.ceoApproval || "pending"),
        ceoApprovedBy: "",
        ceoApprovedAt: "",
        updatedAt: now
      }

      let savedReport: any
      if (currentReport) {
        savedReport = await lumi.entities.weeklyReports.update(currentReport._id, reportData)
        toast.success(submit ? "주간보고가 제출되었습니다" : "주간보고가 수정되었습니다")
      } else {
        savedReport = await lumi.entities.weeklyReports.create({
          ...reportData,
          createdAt: now
        })
        toast.success(submit ? "주간보고가 제출되었습니다" : "주간보고가 저장되었습니다")
      }

      // C-Level 사용자는 팀장에게 알림 보내지 않음
      if (submit && savedReport && !isCLevel) {
        await createNotificationForManagers(savedReport._id)
      }

      loadWeeklyReport()
    } catch (error) {
      console.error("Failed to save report:", error)
      toast.error(submit ? "제출에 실패했습니다" : "저장에 실패했습니다")
    } finally {
      setLoading(false)
    }
  }

  const createNotificationForManagers = async (reportId: string) => {
    if (!user || !userProfile || !reportId) {
      console.error("Missing required data for notification:", { user, userProfile, reportId })
      return
    }
    
    try {
      console.log("Creating notifications for managers with reportId:", reportId)
      
      const managersResult = await lumi.entities.userProfiles3.list({
        filter: {
          teamId: userProfile.teamId,
          role: "MANAGER",
          enabled: true
        }
      })

      console.log("Found managers:", managersResult.list.length)

      const weekStartDate = new Date(selectedWeek)
      weekStartDate.setHours(0, 0, 0, 0)
      const weekStart = weekStartDate.toISOString()
      
      const weekEndDate = getWeekEnd(selectedWeek)
      weekEndDate.setHours(0, 0, 0, 0)
      const weekEnd = weekEndDate.toISOString()

      const now = new Date().toISOString()

      const month = weekStartDate.getMonth() + 1
      const firstDay = new Date(weekStartDate.getFullYear(), weekStartDate.getMonth(), 1)
      const weekNumber = Math.ceil((weekStartDate.getDate() + firstDay.getDay()) / 7)

      for (const manager of managersResult.list) {
        console.log("Creating notification for manager:", manager.userName)
        
        const notification = await lumi.entities.notifications.create({
          userId: manager.userId,
          type: "submitted",
          message: `${userProfile.userName}님이 ${month}월 ${weekNumber}주차 주간보고를 제출했습니다. 승인해주세요.`,
          reportId: reportId,
          authorId: user.userId,
          authorName: userProfile.userName,
          teamId: userProfile.teamId,
          teamName: userProfile.teamName,
          weekStart,
          weekEnd,
          createdAt: now,
          isRead: false,
          isDeleted: false
        })
        
        console.log("Notification created successfully:", notification._id)
      }
      
      console.log("All notifications created successfully")
    } catch (error) {
      console.error("Failed to create notifications:", error)
    }
  }

  const submitReport = async () => {
    if (!currentReport && isHtmlEmpty(thisWeekContent)) {
      toast.error("금주 실적 내용을 먼저 입력해주세요")
      return
    }
    
    if (currentReport?.status === "submitted" && currentReport?.managerApproval !== "rejected" && currentReport?.ceoApproval !== "rejected") {
      toast.error("이미 제출된 보고서입니다")
      return
    }

    const confirmMessage = (currentReport?.managerApproval === "rejected" || currentReport?.ceoApproval === "rejected")
      ? "반려된 보고서를 다시 제출하시겠습니까?\n제출 후에는 팀장 승인부터 다시 받게 됩니다."
      : "주간보고를 제출하시겠습니까?\n제출 후에는 팀장 승인 전까지 수정할 수 없습니다."
    
    const confirmed = window.confirm(confirmMessage)
    if (!confirmed) return

    await saveReport(true)
  }

  const handlePrint = () => {
    window.print()
  }

  // 지난주 차주 계획을 클립보드로 복사 (HTML + 텍스트 동시 복사)
  const copyPrevPlan = async () => {
    if (isHtmlEmpty(prevNextWeekPlan)) {
      toast.error("복사할 지난주 차주 계획이 없습니다")
      return
    }

    const plainText = prevNextWeekPlan
      .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim()

    try {
      if (navigator.clipboard && typeof (window as any).ClipboardItem !== "undefined") {
        const item = new (window as any).ClipboardItem({
          "text/html": new Blob([prevNextWeekPlan], { type: "text/html" }),
          "text/plain": new Blob([plainText], { type: "text/plain" })
        })
        await (navigator.clipboard as any).write([item])
      } else {
        await navigator.clipboard.writeText(plainText)
      }
      toast.success("지난주 차주 계획을 복사했습니다")
    } catch (error) {
      console.error("Failed to copy previous plan:", error)
      toast.error("복사에 실패했습니다. 아래 버튼으로 바로 붙여넣기 해주세요")
    }
  }

  // 지난주 차주 계획을 선택한 에디터의 커서 위치에 바로 삽입
  const insertPrevPlan = (target: "this" | "next") => {
    if (isHtmlEmpty(prevNextWeekPlan)) {
      toast.error("붙여넣을 지난주 차주 계획이 없습니다")
      return
    }

    const editorRef = target === "this" ? quillRef : quillRefNext
    const quill = editorRef.current?.getEditor?.()

    if (!quill) {
      toast.error("에디터가 준비되지 않았습니다. 잠시 후 다시 시도해주세요")
      return
    }

    try {
      const range = quill.getSelection(true)
      const insertIndex =
        range && typeof range.index === "number"
          ? range.index
          : Math.max(0, quill.getLength() - 1)

      quill.clipboard.dangerouslyPasteHTML(insertIndex, prevNextWeekPlan, "user")

      setTimeout(() => {
        try {
          quill.setSelection(Math.max(0, quill.getLength() - 1))
          quill.focus()
        } catch (selectionError) {
          console.warn("Selection update failed:", selectionError)
        }
      }, 0)

      toast.success(target === "this" ? "금주 실적에 붙여넣었습니다" : "차주 계획에 붙여넣었습니다")
    } catch (error) {
      console.error("Failed to insert previous plan:", error)
      toast.error("붙여넣기에 실패했습니다")
    }
  }

  // 연결된 장비 이슈 배지 클릭 시 장비이력 화면의 해당 이슈로 딥링크 이동
  const handleNavigateToIssue = (issue: LinkedIssue) => {
    setPendingIssueTarget({ equipmentId: issue.equipmentId, issueId: issue.issueId })
    window.dispatchEvent(new Event("navigateToEquipmentIssue"))
  }

  const previousWeek = () => {
    const newDate = new Date(selectedWeek)
    newDate.setDate(newDate.getDate() - 7)
    setSelectedWeek(newDate)
  }

  const nextWeek = () => {
    const newDate = new Date(selectedWeek)
    newDate.setDate(newDate.getDate() + 7)
    setSelectedWeek(newDate)
  }

  // 이미지 개수 추적 함수 (useMemo로 최적화)
  const imageCount = useMemo(() => {
    const imgTagRegex = /<img[^>]*>/gi
    const matches = (thisWeekContent || "").match(imgTagRegex)
    return matches ? matches.length : 0
  }, [thisWeekContent])

  // 이미지 업로드 핸들러 (useCallback으로 메모이제이션)
  const imageHandler = useCallback(() => {
    // ref를 사용하여 최신 업로드 상태 확인
    if (uploadingRef.current) {
      toast.error("이미지 업로드 중입니다. 잠시만 기다려주세요.")
      return
    }

    // 현재 content에서 이미지 개수 실시간 계산
    const imgTagRegex = /<img[^>]*>/gi
    const currentContent = quillRef.current?.getEditor()?.root.innerHTML || ""
    const matches = currentContent.match(imgTagRegex)
    const currentImageCount = matches ? matches.length : 0

    if (currentImageCount >= MAX_IMAGES) {
      toast.error(`최대 ${MAX_IMAGES}개의 이미지만 업로드할 수 있습니다.`)
      return
    }

    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/png, image/jpeg, image/jpg')
    input.setAttribute('multiple', 'true')
    input.click()

    input.onchange = async () => {
      const files = input.files
      if (!files || files.length === 0) return

      // 파일 개수 확인 (최신 이미지 개수 다시 계산)
      const latestContent = quillRef.current?.getEditor()?.root.innerHTML || ""
      const latestMatches = latestContent.match(imgTagRegex)
      const latestImageCount = latestMatches ? latestMatches.length : 0
      const remainingSlots = MAX_IMAGES - latestImageCount
      
      if (files.length > remainingSlots) {
        toast.error(`최대 ${remainingSlots}개의 이미지만 더 추가할 수 있습니다.`)
        return
      }

      // 파일 검증
      const validFiles: File[] = []
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        
        // 파일 타입 검증
        if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
          toast.error(`${file.name}: PNG, JPG 형식만 지원됩니다.`)
          continue
        }
        
        // 파일 크기 검증
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`${file.name}: 파일 크기는 10MB 이하여야 합니다.`)
          continue
        }
        
        validFiles.push(file)
      }

      if (validFiles.length === 0) {
        return
      }

      uploadingRef.current = true
      setUploadingImage(true)
      const loadingToast = toast.loading(`이미지 업로드 중... (0/${validFiles.length})`)

      try {
        // Lumi SDK를 사용한 파일 업로드
        const uploadResults = await lumi.tools.file.upload(validFiles)
        
        let successCount = 0
        let failCount = 0

        // 업로드 결과 처리
        uploadResults.forEach((result, index) => {
          toast.loading(`이미지 업로드 중... (${index + 1}/${validFiles.length})`, { id: loadingToast })
          
          if (result.fileUrl) {
            // 성공: 에디터에 이미지 삽입
            try {
              const quill = quillRef.current?.getEditor()
              if (quill) {
                // React 렌더링 사이클과 분리하여 Selection API 호출
                setTimeout(() => {
                  try {
                    const range = quill.getSelection(true)
                    if (range && range.index !== undefined) {
                      quill.insertEmbed(range.index, 'image', result.fileUrl)
                      // setSelection도 안전하게 호출
                      setTimeout(() => {
                        try {
                          quill.setSelection(range.index + 1)
                        } catch (e) {
                          console.warn('Selection update failed:', e)
                        }
                      }, 0)
                    } else {
                      // range가 없으면 마지막에 삽입
                      const length = quill.getLength()
                      quill.insertEmbed(Math.max(0, length - 1), 'image', result.fileUrl)
                    }
                  } catch (selectionErr) {
                    console.warn('Selection error, inserting at end:', selectionErr)
                    const length = quill.getLength()
                    quill.insertEmbed(Math.max(0, length - 1), 'image', result.fileUrl)
                  }
                }, 0)
              }
              successCount++
            } catch (err) {
              console.error('Error inserting image:', err)
              failCount++
            }
          } else {
            // 실패
            console.error(`Upload failed for ${result.fileName}:`, result.uploadError)
            toast.error(`${result.fileName}: ${result.uploadError || '업로드 실패'}`, { duration: 3000 })
            failCount++
          }
        })

        toast.dismiss(loadingToast)
        
        if (successCount > 0) {
          toast.success(`${successCount}개의 이미지가 업로드되었습니다.`)
        }
        
        if (failCount > 0) {
          toast.error(`${failCount}개의 이미지 업로드에 실패했습니다.`)
        }
      } catch (error: any) {
        console.error('Image upload error:', error)
        toast.dismiss(loadingToast)
        const rawMsg = (error?.message || error?.data?.error || '').toString().toLowerCase()
        if (rawMsg.includes('quota')) {
          toast.error('저장 용량 한도를 초과하여 이미지를 업로드할 수 없습니다. 관리자에게 문의해 주세요.', { duration: 6000 })
        } else {
          toast.error('이미지 업로드 중 오류가 발생했습니다.')
        }
      } finally {
        uploadingRef.current = false
        setUploadingImage(false)
      }
    }
  }, []) // 의존성 배열 비움 - MAX_IMAGES는 상수이므로 문제없음

  // 최적화: timeEntries가 바될 때만 재계산 (매 렌더 재계산 방지)
  const getProjectTimeSummary = useCallback(() => {
    const summary: { [projectName: string]: { [activityCode: string]: number } } = {}

    timeEntries.forEach((entry: any) => {
      if (entry.isVacation) return

      if (!entry.projectName || 
          entry.projectName === "연차" || 
          entry.projectName === "오전반차" || 
          entry.projectName === "오후반차") {
        return
      }

      if (!summary[entry.projectName]) {
        summary[entry.projectName] = {}
      }

      if (entry.dailyEntries) {
        entry.dailyEntries.forEach((daily: any) => {
          if (daily.activities) {
            daily.activities.forEach((activity: any) => {
              const code = activity.activityCode
              const hours = activity.hours || 0
              
              if (!code || hours === 0) return
              
              if (!summary[entry.projectName][code]) {
                summary[entry.projectName][code] = 0
              }
              summary[entry.projectName][code] += hours
            })
          }
        })
      }
    })

    return summary
  }, [timeEntries])

  // quillModules를 useMemo로 메모이제이션하여 불필요한 리렌더링 방지
  const quillModules = useMemo(() => {
    return {
      toolbar: {
        container: [
          [{ 'header': [1, 2, 3, false] }],
          [{ 'size': Size.whitelist }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          [{ 'indent': '-1' }, { 'indent': '+1' }],
          [{ 'color': [] }, { 'background': [] }],
          ['link', 'image'],
          ['clean']
        ],
        handlers: {
          image: imageHandler
        }
      },
      keyboard: {
        bindings: {
          // 자동 ordered list 생성 비활성화 (숫자 + 스페이스)
          'list autofill': {
            key: ' ',
            collapsed: true,
            format: { list: false },
            prefix: /^\d+\.$/,
            handler: function() {
              // 기본 동작 무시 - 일반 텍스트로 유지
              return true;
            }
          }
        }
      },
      imageResize: {
        parchment: Quill.import('parchment'),
        modules: ['Resize', 'DisplaySize']
      }
    }
  }, [imageHandler])

  // 차주 계획용 모듈 (이미지 업로드 제외 - 텍스트 중심 계획 작성)
  const quillModulesNext = useMemo(() => {
    return {
      toolbar: {
        container: [
          [{ 'header': [1, 2, 3, false] }],
          [{ 'size': Size.whitelist }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          [{ 'indent': '-1' }, { 'indent': '+1' }],
          [{ 'color': [] }, { 'background': [] }],
          ['clean']
        ]
      },
      keyboard: {
        bindings: {
          'list autofill': {
            key: ' ',
            collapsed: true,
            format: { list: false },
            prefix: /^\d+\.$/,
            handler: function() {
              return true;
            }
          }
        }
      }
    }
  }, [])

  const quillFormats = useMemo(() => [
    'header', "size", "font",
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'indent',
    'color', 'background', 
    'image'
  ], [])

  const projectTimeSummary = useMemo(() => getProjectTimeSummary(), [getProjectTimeSummary])
  const hasTimeData = Object.keys(projectTimeSummary).length > 0

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 p-4 md:p-8">
      <style>{`
        /* Quill 에디터 폰트 크기 스타일 */
        /* 드롭다운 레이블 - 현재 선택된 크기 표시 (모두 16px 크기로 통일) */
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="8px"]::before {
          content: '8px';
          font-size: 16px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="9px"]::before {
          content: '9px';
          font-size: 16px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before {
          content: '10px';
          font-size: 16px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="11px"]::before {
          content: '11px';
          font-size: 16px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before {
          content: '12px';
          font-size: 16px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before {
          content: '14px';
          font-size: 16px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before {
          content: '16px';
          font-size: 16px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before {
          content: '18px';
          font-size: 16px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before {
          content: '24px';
          font-size: 16px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="36px"]::before {
          content: '36px';
          font-size: 16px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="48px"]::before {
          content: '48px';
          font-size: 16px !important;
        }
        
        /* 드롭다운 아이템 - 선택 옵션들 (크기를 제한하여 드롭다운이 튀어나오지 않도록) */
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="8px"]::before {
          content: '8px';
          font-size: 8px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="9px"]::before {
          content: '9px';
          font-size: 9px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before {
          content: '10px';
          font-size: 10px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="11px"]::before {
          content: '11px';
          font-size: 11px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before {
          content: '12px';
          font-size: 12px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before {
          content: '14px';
          font-size: 14px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before {
          content: '16px';
          font-size: 16px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before {
          content: '18px';
          font-size: 18px !important;
        }
        /* 24px 이상은 드롭다운에서 18px로 표시하되 텍스트는 크기 표시 */
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before {
          content: '24px';
          font-size: 18px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="36px"]::before {
          content: '36px';
          font-size: 18px !important;
        }
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="48px"]::before {
          content: '48px';
          font-size: 18px !important;
        }
        
        /* 기본 폰트 크기 설정 (초기 상태에만 적용) */
        .ql-snow .ql-picker.ql-size .ql-picker-label:not([data-value])::before {
          content: '16px' !important;
        }
        
        /* 에디터 내용에 실제 폰트 크기 적용 */
        .ql-editor {
          font-size: 16px; /* 기본 폰트 크기 */
        }
        .ql-editor .ql-size-8px { font-size: 8px; }
        .ql-editor .ql-size-9px { font-size: 9px; }
        .ql-editor .ql-size-10px { font-size: 10px; }
        .ql-editor .ql-size-11px { font-size: 11px; }
        .ql-editor .ql-size-12px { font-size: 12px; }
        .ql-editor .ql-size-14px { font-size: 14px; }
        .ql-editor .ql-size-16px { font-size: 16px; }
        .ql-editor .ql-size-18px { font-size: 18px; }
        .ql-editor .ql-size-24px { font-size: 24px; }
        .ql-editor .ql-size-36px { font-size: 36px; }
        .ql-editor .ql-size-48px { font-size: 48px; }
        
        /* 사용자 지정 높이 적용: 에디터 래퍼(.quill)가 지정 높이를 따르고, 내부 container가 나머지를 채움 */
        .weekly-report-editor .quill {
          display: flex;
          flex-direction: column;
        }
        .weekly-report-editor .ql-container {
          flex: 1 1 auto;
          overflow-y: auto;
        }
        .weekly-report-editor .ql-editor {
          min-height: 100%;
        }

        /* 모바일 화면에서 들여쓰기 줄이기 (핸드폰만) */
        @media only screen and (max-width: 640px) {
          .ql-editor .ql-indent-1 { padding-left: 1em !important; }
          .ql-editor .ql-indent-2 { padding-left: 2em !important; }
          .ql-editor .ql-indent-3 { padding-left: 3em !important; }
          .ql-editor .ql-indent-4 { padding-left: 4em !important; }
          .ql-editor .ql-indent-5 { padding-left: 5em !important; }
          .ql-editor .ql-indent-6 { padding-left: 6em !important; }
          .ql-editor .ql-indent-7 { padding-left: 7em !important; }
          .ql-editor .ql-indent-8 { padding-left: 8em !important; }
        }
        
        @media print {
          @page {
            margin: 1cm;
            size: A4;
          }

          /* 인쇄 시 배경색(회색/푸른색)이 그대로 출력되도록 강제 */
          #printable-content, #printable-content * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          html, body {
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }
          
          body * {
            visibility: hidden;
          }
          
          #printable-content, #printable-content * {
            visibility: visible;
          }
          
          #printable-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: auto !important;
            max-height: none !important;
            min-height: auto !important;
            background: white !important;
            padding: 10px !important;
            margin: 0 !important;
            overflow: visible !important;
            border: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          
          .no-print {
            display: none !important;
          }
          
          .ql-editor {
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            padding: 0 !important;
            margin: 0 !important;
            overflow: visible !important;
            font-size: 16px !important;
          }
          
          .quill {
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            margin: 0 !important;
            overflow: visible !important;
          }
          
          .prose {
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
          }
          
          div, section, article {
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
          }
          
          /* 출력 시 이미지 크기 조정 */
          .ql-editor img, .prose img {
            max-width: 100% !important;
            height: auto !important;
            display: block;
            margin: 10px 0;
            page-break-inside: avoid;
          }
          
          * {
            box-sizing: border-box;
          }
          
          /* 빈 페이지 생성 방지 */
          .prose, .ql-editor {
            page-break-after: auto !important;
            page-break-before: auto !important;
          }
        }
      `}</style>

      <div className="max-w-5xl mx-auto">
        {/* Header - No Print */}
        <div className="no-print mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl shadow-lg">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                  주간보고 작성
                </h1>
                <p className="text-gray-600 mt-1">{userProfile?.teamName} · {userProfile?.userName}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {currentReport?.status === "submitted" && currentReport?.managerApproval !== "rejected" && currentReport?.ceoApproval !== "rejected" ? (
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-2 text-blue-600 font-semibold">
                    <CheckCircle className="w-5 h-5" />
                    {isCLevel ? "제출 완료 (임원)" : "제출 완료 (읽기 전용)"}
                  </span>
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 bg-white text-orange-600 border-2 border-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-all duration-200 shadow-md"
                  >
                    <Printer className="w-5 h-5" />
                    출력
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => saveReport(false)}
                    disabled={loading}
                    className="flex items-center gap-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-orange-700 hover:to-amber-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-5 h-5" />
                    임시저장
                  </button>
                  <button
                    onClick={submitReport}
                    disabled={loading}
                    className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-5 h-5" />
                    제출
                  </button>
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 bg-white text-orange-600 border-2 border-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-all duration-200 shadow-md"
                  >
                    <Printer className="w-5 h-5" />
                    출력
                  </button>
                </>
              )}
            </div>
          </div>



          {/* Week Navigation */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <button
                onClick={previousWeek}
                className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-orange-600" />
                <span className="text-lg font-semibold text-gray-900">
                  {formatDate(selectedWeek)} ~ {formatDate(getWeekEnd(selectedWeek))}
                </span>
                {!isCLevel && currentReport?.managerApproval === "approved" && currentReport?.ceoApproval !== "approved" && (
                  <span className="flex items-center gap-1 text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                    <CheckCircle className="w-4 h-4" />
                    팀장 승인 완료 (CEO 최종 승인 대기)
                  </span>
                )}
                {!isCLevel && currentReport?.ceoApproval === "approved" && (
                  <span className="flex items-center gap-1 text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full">
                    <CheckCircle className="w-4 h-4" />
                    대표이사 승인 완료 ✅
                  </span>
                )}
                {!isCLevel && currentReport?.status === "submitted" && currentReport?.managerApproval === "pending" && (
                  <span className="flex items-center gap-1 text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                    <Clock className="w-4 h-4" />
                    승인대기
                  </span>
                )}
                {!isCLevel && currentReport?.managerApproval === "rejected" && (
                  <span className="flex items-center gap-1 text-sm bg-red-100 text-red-700 px-3 py-1 rounded-full">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    반려됨
                  </span>
                )}
              </div>

              <button
                onClick={nextWeek}
                className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Printable Content */}
        <div id="printable-content" className="bg-white rounded-xl shadow-lg p-8">
          {/* Print Header */}
          <div className="hidden print:block mb-6 pb-4">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-bold text-gray-900">주간 업무 보고</h1>
              
              {/* 결재란 */}
              <table className="border-2 border-gray-900" style={{ borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td className="border-2 border-gray-900 px-2 text-center text-sm font-bold bg-white align-middle" rowSpan={3} style={{ minWidth: '30px', writingMode: 'vertical-rl', textOrientation: 'upright', letterSpacing: '0.1em' }}>결재</td>
                    <th className="border-2 border-gray-900 px-4 py-1 text-sm font-bold bg-white" style={{ minWidth: '60px' }}>작성</th>
                    <th className="border-2 border-gray-900 px-4 py-1 text-sm font-bold bg-white" style={{ minWidth: '60px' }}>검토</th>
                    <th className="border-2 border-gray-900 px-4 py-1 text-sm font-bold bg-white" style={{ minWidth: '60px' }}>확인</th>
                  </tr>
                  <tr>
                    <td className="border-2 border-gray-900 px-4 text-center text-sm" style={{ height: '40px' }}>
                      {currentReport?.status === "submitted" ? (userProfile?.userName || currentReport?.userName || "") : ""}
                    </td>
                    <td className="border-2 border-gray-900 px-4 text-center text-sm" style={{ height: '40px' }}>
                      {currentReport?.managerApproval === "approved" ? approvalManagerName : ""}
                    </td>
                    <td className="border-2 border-gray-900 px-4 text-center text-sm" style={{ height: '40px' }}>
                      {currentReport?.ceoApproval === "approved" ? ceoApproverName : ""}
                    </td>
                  </tr>
                  <tr>
                    <td className="border-2 border-gray-900 px-4 text-center text-xs text-gray-600" style={{ height: '30px' }}>
                      {currentReport?.submittedAt ? formatDateYMD(new Date(currentReport.submittedAt)) : ""}
                    </td>
                    <td className="border-2 border-gray-900 px-4 text-center text-xs text-gray-600" style={{ height: '30px' }}>
                      {currentReport?.approvedAt ? formatDateYMD(new Date(currentReport.approvedAt)) : ""}
                    </td>
                    <td className="border-2 border-gray-900 px-4 text-center text-xs text-gray-600" style={{ height: '30px' }}>
                      {currentReport?.ceoApprovedAt ? formatDateYMD(new Date(currentReport.ceoApprovedAt)) : ""}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            {/* 보고서 정보 박스 */}
            <div className="border border-gray-900 p-4 mb-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="font-semibold">작성자:</span> {userProfile?.userName}</div>
                <div><span className="font-semibold">부서:</span> {userProfile?.teamName}</div>
                <div><span className="font-semibold">작성기간:</span> {formatDateMonthDay(selectedWeek)} ~ {formatDateMonthDay(getWeekEnd(selectedWeek))}</div>
                <div><span className="font-semibold">작성일:</span> {currentReport ? formatDateOnly(new Date(currentReport.updatedAt)) : formatDateOnly(new Date())}</div>
                {!isCLevel && (
                  <div>
                    <span className="font-semibold">승인 상태:</span>{" "}
                    {currentReport?.managerApproval === "approved" && "✓ 팀장 승인 완료"}
                    {currentReport?.status === "submitted" && currentReport?.managerApproval === "pending" && "⏱ 승인 대기 중"}
                    {currentReport?.managerApproval === "rejected" && "✗ 반려됨"}
                    {!currentReport && "미제출"}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Editor / Content */}
          <div className="mb-6">
            {currentReport?.status === "submitted" && currentReport?.managerApproval !== "rejected" && currentReport?.ceoApproval !== "rejected" ? (
              <>
                {(currentReport?.thisWeekContent || currentReport?.nextWeekPlan) ? (
                  <div className="space-y-6">
                    {/* 금주 실적 */}
                    <div className="prose max-w-none border border-gray-200 rounded-lg p-6 bg-gray-50">
                      <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-3 border-b border-gray-300 flex items-center gap-2">
                        <span className="inline-block w-1.5 h-6 bg-orange-500 rounded" />
                        금주 실적
                      </h2>
                      {currentReport?.thisWeekContent ? (
                        <div className="ql-editor" dangerouslySetInnerHTML={{ __html: currentReport.thisWeekContent }} />
                      ) : (
                        <p className="text-gray-500 italic">작성된 금주 실적이 없습니다.</p>
                      )}
                    </div>
                    {/* 차주 계획 */}
                    <div className="prose max-w-none border border-gray-200 rounded-lg p-6 bg-blue-50">
                      <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-3 border-b border-gray-300 flex items-center gap-2">
                        <span className="inline-block w-1.5 h-6 bg-blue-500 rounded" />
                        차주 계획
                      </h2>
                      {currentReport?.nextWeekPlan ? (
                        <div className="ql-editor" dangerouslySetInnerHTML={{ __html: currentReport.nextWeekPlan }} />
                      ) : (
                        <p className="text-gray-500 italic">작성된 차주 계획이 없습니다.</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="prose max-w-none border border-gray-200 rounded-lg p-6 bg-gray-50">
                    <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-3 border-b border-gray-300">보고 내용</h2>
                    {content ? (
                      <div className="ql-editor" dangerouslySetInnerHTML={{ __html: content }} />
                    ) : (
                      <p className="text-gray-500 italic">작성된 내용이 없습니다.</p>
                    )}
                  </div>
                )}

                {/* 제출 후 첨부파일 조회 섹션 */}
                {attachments.length > 0 && (
                  <div className="no-print mt-6">
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                          <Paperclip className="w-5 h-5 text-orange-600" />
                          첨부파일 ({attachments.length})
                        </h3>
                      </div>

                      <div className="space-y-2">
                        {attachments.map((attachment) => (
                          <div
                            key={attachment._id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <span className="text-2xl flex-shrink-0">{getFileIcon(attachment.fileType, attachment.fileName)}</span>
                              <div className="flex-1 min-w-0">
                                <a
                                  href={attachment.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium text-gray-900 hover:text-orange-600 truncate block"
                                >
                                  {attachment.fileName}
                                </a>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(attachment.fileSize)} · {new Date(attachment.uploadedAt).toLocaleDateString('ko-KR')}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* 제출 후 연결된 장비 이슈 (읽기전용) */}
                {linkedIssues.length > 0 && (
                  <div className="no-print mt-6">
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                        <Link2 className="w-5 h-5 text-orange-600" />
                        관련 장비 이슈 ({linkedIssues.length})
                      </h3>
                      <LinkedIssueBadges
                        issues={linkedIssues}
                        onNavigate={handleNavigateToIssue}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* CEO/팀장 반려 사유 표시 */}
                {(managerRejection || ceoRejection) && (
                  <div className="no-print">
                    <div className="bg-red-50 border-2 border-red-500 rounded-xl p-6 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-red-900 mb-2">⚠️ 주간보고가 반려되었습니다</h3>
                          <p className="text-red-800 mb-3">
                            내용을 수정하여 다시 제출해주세요. (다시 제출 시 팀장 승인부터 받게 됩니다)
                          </p>
                          
                          {/* 팀장 반려 사유 */}
                          {managerRejection && (
                            <div className="bg-white rounded-lg p-4 border border-red-200 mb-3">
                              <div className="flex items-start justify-between mb-2">
                                <p className="text-sm font-semibold text-red-700">📋 팀장 반려 사유:</p>
                                <div className="text-xs text-gray-500">
                                  {managerRejection.managerName && (
                                    <span>반려자: {managerRejection.managerName} • </span>
                                  )}
                                  {managerRejection.rejectedAt && (
                                    <span>반려일시: {formatDateTime(new Date(managerRejection.rejectedAt))}</span>
                                  )}
                                </div>
                              </div>
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{managerRejection.reason}</p>
                            </div>
                          )}
                          
                          {/* CEO 반려 사유 */}
                          {ceoRejection && (
                            <div className="bg-white rounded-lg p-4 border border-red-200">
                              <div className="flex items-start justify-between mb-2">
                                <p className="text-sm font-semibold text-red-700">🏢 대표이사 반려 사유:</p>
                                <div className="text-xs text-gray-500">
                                  {ceoRejection.userName && (
                                    <span>반려자: {ceoRejection.userName} • </span>
                                  )}
                                  {ceoRejection.createdAt && (
                                    <span>반려일시: {formatDateTime(new Date(ceoRejection.createdAt))}</span>
                                  )}
                                </div>
                              </div>
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{ceoRejection.reason}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* 지난주 차주 계획 참조 카드 */}
                {!isHtmlEmpty(prevNextWeekPlan) && (
                  <div className="no-print mb-6">
                    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-indigo-600" />
                          <h3 className="text-sm font-semibold text-indigo-800">지난주에 작성한 &lsquo;차주 계획&rsquo; (참고)</h3>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={copyPrevPlan}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-white text-indigo-700 border border-indigo-300 rounded-lg hover:bg-indigo-100 transition-colors"
                            title="내용을 클립보드에 복사"
                          >
                            <Copy className="w-3.5 h-3.5" />
                            복사
                          </button>
                          <button
                            type="button"
                            onClick={() => insertPrevPlan('this')}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                            title="금주 실적 에디터에 바로 붙여넣기"
                          >
                            <ClipboardPaste className="w-3.5 h-3.5" />
                            금주 실적에 붙여넣기
                          </button>
                          <button
                            type="button"
                            onClick={() => insertPrevPlan('next')}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            title="차주 계획 에디터에 바로 붙여넣기"
                          >
                            <ClipboardPaste className="w-3.5 h-3.5" />
                            차주 계획에 붙여넣기
                          </button>
                        </div>
                      </div>
                      <div
                        className="ql-editor bg-white rounded-lg border border-indigo-100 p-3 max-h-56 overflow-y-auto text-sm select-text"
                        style={{ padding: '12px', userSelect: 'text' }}
                        dangerouslySetInnerHTML={{ __html: prevNextWeekPlan }}
                      />
                      <p className="text-xs text-indigo-500 mt-2">지난주에 계획한 항목을 참고하여 이번 주 실적을 작성하세요. 내용을 드래기해 직접 복사하거나, 위 버튼으로 에디터에 바로 붙여넣을 수 있습니다.</p>
                    </div>
                  </div>
                )}

                {/* 금주 실적 에디터 */}
                <div className="no-print">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      <span className="inline-block w-1.5 h-6 bg-orange-500 rounded" />
                      금주 실적
                    </h2>
                    {/* 에디터 높이 조절 버튼 */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">에디터 높이</span>
                      <button
                        type="button"
                        onClick={() => setThisWeekHeight(h => Math.max(240, h - 120))}
                        className="px-2 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        title="에디터 줄이기"
                      >
                        − 줄이기
                      </button>
                      <button
                        type="button"
                        onClick={() => setThisWeekHeight(h => Math.min(1200, h + 120))}
                        className="px-2 py-1 text-sm bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                        title="에디터 늘리기"
                      >
                        + 늘리기
                      </button>
                      <button
                        type="button"
                        onClick={() => setThisWeekHeight(384)}
                        className="px-2 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        title="기본 높이로 초기화"
                      >
                        초기화
                      </button>
                    </div>
                  </div>
                  {/* 이미지 업로드 안내 */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-blue-700">
                        <ImageIcon className="w-4 h-4" />
                        <span>이미지 첨부: {imageCount}/{MAX_IMAGES}개 (PNG, JPG, 10MB 이하)</span>
                      </div>
                      {uploadingImage && (
                        <div className="flex items-center gap-2 text-sm text-orange-600">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-600 border-t-transparent"></div>
                          <span>업로드 중...</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="weekly-report-editor">
                    <ReactQuill
                      ref={quillRef}
                      key={`quill-this-${selectedWeek.toISOString()}`}
                      theme="snow"
                      value={thisWeekContent}
                      onChange={setThisWeekContent}
                      modules={quillModules}
                      formats={quillFormats}
                      style={{ height: `${thisWeekHeight}px` }}
                      preserveWhitespace
                    />
                    {/* 드래그로 높이 조절하는 핸들 */}
                    <div
                      onMouseDown={(e) => {
                        e.preventDefault()
                        const startY = e.clientY
                        const startHeight = thisWeekHeight
                        const onMove = (moveEvent: MouseEvent) => {
                          const delta = moveEvent.clientY - startY
                          setThisWeekHeight(Math.min(1200, Math.max(240, startHeight + delta)))
                        }
                        const onUp = () => {
                          window.removeEventListener('mousemove', onMove)
                          window.removeEventListener('mouseup', onUp)
                        }
                        window.addEventListener('mousemove', onMove)
                        window.addEventListener('mouseup', onUp)
                      }}
                      className="flex items-center justify-center h-5 cursor-ns-resize bg-gray-100 hover:bg-orange-100 border border-t-0 border-gray-300 rounded-b-lg transition-colors select-none"
                      title="드래그하여 에디터 높이 조절"
                    >
                      <div className="w-10 h-1 bg-gray-400 rounded-full" />
                    </div>
                  </div>
                </div>

                {/* 차주 계획 에디터 */}
                <div className="no-print mt-8">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      <span className="inline-block w-1.5 h-6 bg-blue-500 rounded" />
                      차주 계획
                      <span className="text-sm font-normal text-red-500">* 제출 시 필수</span>
                    </h2>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">에디터 높이</span>
                      <button
                        type="button"
                        onClick={() => setNextWeekHeight(h => Math.max(240, h - 120))}
                        className="px-2 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        title="에디터 줄이기"
                      >
                        − 줄이기
                      </button>
                      <button
                        type="button"
                        onClick={() => setNextWeekHeight(h => Math.min(1200, h + 120))}
                        className="px-2 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                        title="에디터 늘리기"
                      >
                        + 늘리기
                      </button>
                      <button
                        type="button"
                        onClick={() => setNextWeekHeight(320)}
                        className="px-2 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        title="기본 높이로 초기화"
                      >
                        초기화
                      </button>
                    </div>
                  </div>
                  <div className="weekly-report-editor">
                    <ReactQuill
                      ref={quillRefNext}
                      key={`quill-next-${selectedWeek.toISOString()}`}
                      theme="snow"
                      value={nextWeekPlan}
                      onChange={setNextWeekPlan}
                      modules={quillModulesNext}
                      formats={quillFormats}
                      style={{ height: `${nextWeekHeight}px` }}
                      preserveWhitespace
                    />
                    <div
                      onMouseDown={(e) => {
                        e.preventDefault()
                        const startY = e.clientY
                        const startHeight = nextWeekHeight
                        const onMove = (moveEvent: MouseEvent) => {
                          const delta = moveEvent.clientY - startY
                          setNextWeekHeight(Math.min(1200, Math.max(240, startHeight + delta)))
                        }
                        const onUp = () => {
                          window.removeEventListener('mousemove', onMove)
                          window.removeEventListener('mouseup', onUp)
                        }
                        window.addEventListener('mousemove', onMove)
                        window.addEventListener('mouseup', onUp)
                      }}
                      className="flex items-center justify-center h-5 cursor-ns-resize bg-gray-100 hover:bg-blue-100 border border-t-0 border-gray-300 rounded-b-lg transition-colors select-none"
                      title="드래그하여 에디터 높이 조절"
                    >
                      <div className="w-10 h-1 bg-gray-400 rounded-full" />
                    </div>
                  </div>
                </div>

                {/* 관련 장비 이슈 섹션 */}
                <div className="no-print mt-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Link2 className="w-5 h-5 text-orange-600" />
                        관련 장비 이슈 ({linkedIssues.length})
                      </h3>
                      <button
                        type="button"
                        onClick={() => setShowIssuePicker(true)}
                        className="flex items-center gap-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white px-4 py-2 rounded-lg font-medium hover:from-orange-700 hover:to-amber-700 transition-all duration-200 shadow"
                      >
                        <Link2 className="w-4 h-4" />
                        이슈 연결
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">🔗 장비 이력에서 관련 이슈를 연결하면, 주간보고 검토 시 해당 이슈로 바로 이동할 수 있습니다.</p>
                    {linkedIssues.length > 0 ? (
                      <LinkedIssueBadges
                        issues={linkedIssues}
                        onRemove={(issueId) => setLinkedIssues(prev => prev.filter(i => i.issueId !== issueId))}
                      />
                    ) : (
                      <div className="text-center py-6 text-gray-500">
                        <Link2 className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">연결된 장비 이슈가 없습니다</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* 첨부파일 섹션 */}
                <div className="no-print mt-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Paperclip className="w-5 h-5 text-orange-600" />
                        첨부파일 ({attachments.length}/{MAX_ATTACHMENTS})
                      </h3>
                      {(currentReport?.status !== "submitted" || currentReport?.managerApproval === "rejected" || currentReport?.ceoApproval === "rejected") && (
                        <label className="flex items-center gap-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white px-4 py-2 rounded-lg font-medium hover:from-orange-700 hover:to-amber-700 transition-all duration-200 shadow cursor-pointer">
                          <Paperclip className="w-4 h-4" />
                          파일 선택
                          <input
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.hwp,.hwpx,.txt,.png,.jpg,.jpeg"
                            onChange={handleFileUpload}
                            className="hidden"
                            disabled={uploadingFile || attachments.length >= MAX_ATTACHMENTS}
                          />
                        </label>
                      )}
                    </div>

                    <div className="text-sm text-gray-600 mb-4">
                      💡 지원 형식: PDF, Word, Excel, PowerPoint, 한글(HWP/HWPX), ZIP, TXT, 이미지 (최대 10MB)
                      {!currentReport?._id && <span className="text-orange-600 font-semibold"> · 파일 업로드는 보고서 임시저장 후 가능합니다</span>}
                    </div>

                    {uploadingFile && (
                      <div className="flex items-center gap-2 text-sm text-orange-600 mb-4">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-600 border-t-transparent"></div>
                        <span>업로드 중...</span>
                      </div>
                    )}

                    {attachments.length > 0 ? (
                      <div className="space-y-2">
                        {attachments.map((attachment) => (
                          <div
                            key={attachment._id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <span className="text-2xl flex-shrink-0">{getFileIcon(attachment.fileType, attachment.fileName)}</span>
                              <div className="flex-1 min-w-0">
                                <a
                                  href={attachment.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium text-gray-900 hover:text-orange-600 truncate block"
                                >
                                  {attachment.fileName}
                                </a>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(attachment.fileSize)} · {new Date(attachment.uploadedAt).toLocaleDateString('ko-KR')}
                                </p>
                              </div>
                            </div>
                            {currentReport?.status !== "submitted" && (
                              <button
                                onClick={() => handleDeleteAttachment(attachment)}
                                className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="삭제"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Paperclip className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">첨부된 파일이 없습니다</p>
                      </div>
                    )}
                  </div>
                </div>
                {/* 편집 중 인쇄용 블록: 편집 중인 금주 실적/차주 계획을 2단으로 출력 (둘 다 비면 레거시 content fallback) */}
                <div className="hidden print:block">
                  {(!isHtmlEmpty(thisWeekContent) || !isHtmlEmpty(nextWeekPlan)) ? (
                    <div className="space-y-6">
                      <div className="prose max-w-none border border-gray-200 rounded-lg p-6 bg-gray-50">
                        <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-3 border-b border-gray-300">금주 실적</h2>
                        {!isHtmlEmpty(thisWeekContent) ? (
                          <div className="ql-editor" dangerouslySetInnerHTML={{ __html: thisWeekContent }} />
                        ) : (
                          <p className="text-gray-500 italic">작성된 금주 실적이 없습니다.</p>
                        )}
                      </div>
                      <div className="prose max-w-none border border-gray-200 rounded-lg p-6 bg-blue-50">
                        <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-3 border-b border-gray-300">차주 계획</h2>
                        {!isHtmlEmpty(nextWeekPlan) ? (
                          <div className="ql-editor" dangerouslySetInnerHTML={{ __html: nextWeekPlan }} />
                        ) : (
                          <p className="text-gray-500 italic">작성된 차주 계획이 없습니다.</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="prose max-w-none border border-gray-200 rounded-lg p-6 bg-gray-50">
                      <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-3 border-b border-gray-300">보고 내용</h2>
                      <div className="ql-editor" dangerouslySetInnerHTML={{ __html: content }} />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

        </div>

        {/* Last Updated Info - No Print */}
        {currentReport && (
          <div className="no-print mt-4 text-center text-sm text-gray-600">
            마지막 수정: {formatDate(new Date(currentReport.updatedAt))}
          </div>
        )}
      </div>

      {/* 이슈 선택 모달 */}
      {showIssuePicker && (
        <IssueLinkPickerModal
          onClose={() => setShowIssuePicker(false)}
          onSelect={(issue) => {
            setLinkedIssues(prev => (
              prev.some(i => i.issueId === issue.issueId)
                ? prev
                : [...prev, issue]
            ))
            setShowIssuePicker(false)
          }}
        />
      )}
    </div>
  )
}

function getMonday(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  return new Date(d.setDate(diff))
}

function getWeekEnd(startDate: Date): Date {
  const end = new Date(startDate)
  end.setDate(end.getDate() + 6)
  return end
}

function formatDate(date: Date): string {
  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).replace(/\. /g, ".").replace(".", "년 ").replace(".", "월 ").replace(".", "일")
}

// 출력용: 월/일만 표시
function formatDateMonthDay(date: Date): string {
  return date.toLocaleDateString("ko-KR", {
    month: "2-digit",
    day: "2-digit"
  }).replace(/\. /g, ".").replace(".", "월 ").replace(".", "일")
}

// 출력용: 년/월/일만 표시 (시간 제외)
function formatDateOnly(date: Date): string {
  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).replace(/\. /g, ".").replace(".", "년 ").replace(".", "월 ").replace(".", "일")
}

function formatDateTimeWithSeconds(date: Date): string {
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).replace(/\. /g, ".").replace(".", "년 ").replace(".", "월 ").replace(".", "일 ")
}

function formatDateTime(date: Date): string {
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).replace(/\. /g, ".").replace(".", "년 ").replace(".", "월 ").replace(".", "일 ")
}

// YYYY/MM/DD 형식으로 날짜 포맷 (결재란 날짜 표시용)
function formatDateYMD(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}/${month}/${day}`
}
